import "./CustomSpeedControl.css";
import React, { useState, useRef, useEffect, useCallback } from "react";

const CustomSpeedControl = ({ playbackRate, onChange }) => {
  const [showSpeed, setShowSpeed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [localSpeed, setLocalSpeed] = useState(playbackRate);
  const speedControlRef = useRef(null);
  const sliderRef = useRef(null);
  const speedTimeoutRef = useRef(null);

  const MIN_SPEED = 0.2;
  const MAX_SPEED = 3;
  const STEP = 0.01;

  const speedToPercentage = useCallback((speed) => {
    return ((speed - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)) * 100;
  }, []);

  const percentageToSpeed = useCallback((percentage) => {
    const rawSpeed = MIN_SPEED + (percentage / 100) * (MAX_SPEED - MIN_SPEED);
    return Math.round(rawSpeed / STEP) * STEP;
  }, []);

  useEffect(() => {
    return () => {
      if (speedTimeoutRef.current) clearTimeout(speedTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (speedTimeoutRef.current) clearTimeout(speedTimeoutRef.current);
    setShowSpeed(true);
  };

  const handleMouseLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    const speedContainer = speedControlRef.current?.querySelector(
      ".custom-speed-popup",
    );

    if (!speedContainer || !speedContainer.contains(relatedTarget)) {
      speedTimeoutRef.current = setTimeout(() => {
        setShowSpeed(false);
      }, 300);
    }
  };

  const handlePopupMouseLeave = () => {
    speedTimeoutRef.current = setTimeout(() => {
      const wrapper = speedControlRef.current;
      const isHoveringWrapper = wrapper?.matches(":hover");
      const isHoveringPopup = wrapper
        ?.querySelector(".custom-speed-popup")
        ?.matches(":hover");

      if (!isHoveringWrapper && !isHoveringPopup) {
        setShowSpeed(false);
      }
    }, 300);
  };

  // 👈 تابع مشترک برای محاسبه سرعت از موقعیت موس/تاچ
  const updateSpeedFromPosition = useCallback(
    (clientY) => {
      if (!sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      let y = clientY - rect.top;
      y = Math.min(rect.height, Math.max(0, y));

      const percentage = (1 - y / rect.height) * 100;
      const newSpeed = percentageToSpeed(percentage);

      setLocalSpeed(newSpeed);
      onChange(newSpeed);
    },
    [percentageToSpeed, onChange],
  );

  // ============== هندلرهای موس ==============
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    updateSpeedFromPosition(e.clientY);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      updateSpeedFromPosition(e.clientY);
    },
    [isDragging, updateSpeedFromPosition],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ============== هندلرهای تاچ (موبایل) ==============
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    e.stopPropagation();

    // نمایش پاپ‌آپ
    if (speedTimeoutRef.current) clearTimeout(speedTimeoutRef.current);
    setShowSpeed(true);
    setIsDragging(true);
    updateSpeedFromPosition(e.touches[0].clientY);
  };

  const handleDbtnTouchStart = (e) => {
    e.stopPropagation();
    if (speedTimeoutRef.current) clearTimeout(speedTimeoutRef.current);
    const newShowSpeed = !showSpeed;
    setShowSpeed(newShowSpeed);
    // 👈 به Controls بگو وضعیت پاپ‌آپ تغییر کرد
  };

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging || e.touches.length !== 1) return;
      e.preventDefault();
      updateSpeedFromPosition(e.touches[0].clientY);
    },
    [isDragging, updateSpeedFromPosition],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    // مخفی کردن پاپ‌آپ بعد از ۱ ثانیه
    speedTimeoutRef.current = setTimeout(() => {
      setShowSpeed(false);
    }, 1000);
  }, []);

  // 👈 ثبت event listenerهای تاچ روی document
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    }
    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, handleTouchMove, handleTouchEnd]);

  const handleReset = (e) => {
    e.stopPropagation();
    onChange(1);
    setLocalSpeed(1);
    setShowSpeed(false);
  };

  const percentage = speedToPercentage(localSpeed);

  return (
    <div
      ref={speedControlRef}
      className="speed-control"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="control-btn speed-btn"
        onTouchStart={handleDbtnTouchStart}
      >
        <i className="fas fa-tachometer-alt"></i>
        <span className="speed-badge">{playbackRate.toFixed(2)}x</span>
      </button>

      {showSpeed && (
        <div
          className="custom-speed-popup"
          onMouseLeave={handlePopupMouseLeave}
          onTouchStart={(e) => e.stopPropagation()} // جلوگیری از بسته شدن ناخواسته
        >
          <div className="custom-speed-display">{localSpeed.toFixed(2)}x</div>

          <div className="custom-speed-slider-container">
            <div
              ref={sliderRef}
              className={`custom-speed-slider ${isDragging ? "dragging" : ""}`}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <div
                className="custom-speed-level"
                style={{ height: `${percentage}%` }}
              />
              <div
                className="custom-speed-thumb"
                style={{ bottom: `${percentage}%` }}
              />
            </div>
            <div className="custom-speed-labels">
              <span>3</span>
              <span className="empty-label"></span>
              <span>0.2</span>
            </div>
          </div>

          <button className="custom-speed-reset" onClick={handleReset}>
            ریست
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomSpeedControl;
